/**
 * Generic clipboard service for storing and retrieving data in localStorage.
 * This service is framework-agnostic and can be used for any type of data.
 */
class ClipboardService {
    private static instance: ClipboardService;
    private readonly STORAGE_KEY = "regal_clipboard_data";

    private constructor() {}

    public static getInstance(): ClipboardService {
        if (!ClipboardService.instance) {
            ClipboardService.instance = new ClipboardService();
        }
        return ClipboardService.instance;
    }

    /**
     * Copy data to clipboard (localStorage)
     * @param data - Generic data to store
     */
    public copy<T>(data: T): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        } catch {
            // Failed to copy to clipboard - silently fail
        }
    }

    /**
     * Paste data from clipboard (localStorage)
     * @returns The stored data or null if no data exists
     */
    public paste<T>(): T | null {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data ? JSON.parse(data) : null;
        } catch {
            return null;
        }
    }

    /**
     * Clear clipboard data
     */
    public clear(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch {
            // Failed to clear clipboard - silently fail
        }
    }

    /**
     * Check if clipboard has data
     * @returns true if data exists, false otherwise
     */
    public hasData(): boolean {
        return localStorage.getItem(this.STORAGE_KEY) !== null;
    }
}

export const clipboardService = ClipboardService.getInstance();
